import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import LocationPickerModal from "../components/LocationPickerModal";

const NO_LOCATION_KEY = "__NO_LOCATION__";

const DAYS = [
  "NA",
  ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
];

const MONTHS = [
  "NA",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

const YEARS = [
  "NA",
  ...Array.from({ length: 80 }, (_, i) => String(new Date().getFullYear() - i)),
];
function getLocationKey(location) {
  const clean = (location || "").trim();
  return clean || NO_LOCATION_KEY;
}

function getLocationLabel(locationKey) {
  return locationKey === NO_LOCATION_KEY ? "No location" : locationKey;
}

function normalizeDate(d) {
  return {
    day: d?.day || "NA",
    month: d?.month || "NA",
    year: d?.year || "NA",
  };
}

function hasMappedLocation(form) {
  return form?.locationLat != null && form?.locationLng != null;
}

function makeLocationPatch(locationData) {
  return {
    location: locationData.name,
    locationName: locationData.name,
    locationLat: locationData.lat,
    locationLng: locationData.lng,
    locationCountry: locationData.country || "",
    locationAdminArea: locationData.adminArea || "",
    locationCity: locationData.city || "",
    locationPlaceId: locationData.placeId || "",
  };
}

function formToLocationData(form) {
  if (!form?.locationLat || !form?.locationLng) return null;

  return {
    name: form.locationName || form.location || "Selected location",
    lat: form.locationLat,
    lng: form.locationLng,
    country: form.locationCountry || "",
    adminArea: form.locationAdminArea || "",
    city: form.locationCity || "",
    placeId: form.locationPlaceId || "",
  };
}

export default function LocationMigrationPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [status, setStatus] = useState("");
  const [migrationModal, setMigrationModal] = useState(null);
  const [dateDrafts, setDateDrafts] = useState({});

  async function scanLocations() {
    if (!auth.currentUser) return;

    setLoading(true);
    setStatus("");

    const snap = await getDocs(
      collection(db, "users", auth.currentUser.uid, "species"),
    );

    const found = [];

    snap.docs.forEach((speciesDoc) => {
      const data = speciesDoc.data();
      const forms = Array.isArray(data.forms) ? data.forms : [];

      forms.forEach((form, formIndex) => {
        const location = (form.locationName || form.location || "").trim();

        if (!hasMappedLocation(form)) {
          found.push({
            speciesId: speciesDoc.id,
            speciesName: data.name || speciesDoc.id,
            commonName: data.common || "",
            formIndex,
            location,
            locationKey: getLocationKey(location),
            label: form.label || "",
            date: form.date || null,
            variant: form.variant || "",
            collections: Array.isArray(form.collections)
              ? form.collections
              : [],
            imageUrl: form.url || "",
            form,
          });
        }
      });
    });

    setRows(found);
    setLoading(false);
  }

  const locationCounts = useMemo(() => {
    const counts = {};

    rows.forEach((row) => {
      counts[row.locationKey] = (counts[row.locationKey] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([locationKey, count]) => ({
        locationKey,
        location: getLocationLabel(locationKey),
        count,
      }))
      .sort((a, b) => {
        if (a.locationKey === NO_LOCATION_KEY) return -1;
        if (b.locationKey === NO_LOCATION_KEY) return 1;
        return b.count - a.count;
      });
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!selectedGroupKey) return rows;
    return rows.filter((row) => row.locationKey === selectedGroupKey);
  }, [rows, selectedGroupKey]);

  async function applyLocationToGroup(locationData) {
    if (!auth.currentUser || !selectedGroupKey || !locationData) return;

    const targetRows = rows.filter(
      (row) => row.locationKey === selectedGroupKey,
    );

    setLoading(true);
    setStatus(`Updating ${targetRows.length} forms...`);

    const rowsBySpecies = targetRows.reduce((acc, row) => {
      if (!acc[row.speciesId]) acc[row.speciesId] = [];
      acc[row.speciesId].push(row);
      return acc;
    }, {});

    for (const [speciesId, speciesRows] of Object.entries(rowsBySpecies)) {
      const speciesRef = doc(
        db,
        "users",
        auth.currentUser.uid,
        "species",
        speciesId,
      );

      const speciesSnap = await getDoc(speciesRef);
      if (!speciesSnap.exists()) continue;

      const data = speciesSnap.data();
      const forms = Array.isArray(data.forms) ? [...data.forms] : [];
      const targetIndexes = new Set(speciesRows.map((row) => row.formIndex));

      const updatedForms = forms.map((form, index) => {
        if (!targetIndexes.has(index)) return form;
        if (hasMappedLocation(form)) return form;

        return {
          ...form,
          ...makeLocationPatch(locationData),
        };
      });

      await updateDoc(speciesRef, { forms: updatedForms });
    }

    setStatus(`Updated ${targetRows.length} forms.`);
    setSelectedGroupKey("");
    setMigrationModal(null);

    await scanLocations();

    setLoading(false);
  }

  async function applyLocationToSingleForm(row, locationData) {
    if (!auth.currentUser || !row || !locationData) return;

    setLoading(true);
    setStatus(`Updating ${row.speciesName}...`);

    const speciesRef = doc(
      db,
      "users",
      auth.currentUser.uid,
      "species",
      row.speciesId,
    );

    const speciesSnap = await getDoc(speciesRef);

    if (!speciesSnap.exists()) {
      setStatus("Species document not found.");
      setLoading(false);
      return;
    }

    const data = speciesSnap.data();
    const forms = Array.isArray(data.forms) ? [...data.forms] : [];

    const updatedForms = forms.map((form, index) =>
      index === row.formIndex
        ? {
            ...form,
            ...makeLocationPatch(locationData),
          }
        : form,
    );

    await updateDoc(speciesRef, { forms: updatedForms });

    setStatus(`Updated ${row.speciesName}.`);
    setMigrationModal(null);

    await scanLocations();

    setLoading(false);
  }

  async function saveSingleFormDate(row, dateData) {
    if (!auth.currentUser || !row || !dateData) return;

    setLoading(true);
    setStatus(`Updating date for ${row.speciesName}...`);

    const speciesRef = doc(
      db,
      "users",
      auth.currentUser.uid,
      "species",
      row.speciesId,
    );

    const speciesSnap = await getDoc(speciesRef);

    if (!speciesSnap.exists()) {
      setStatus("Species document not found.");
      setLoading(false);
      return;
    }

    const data = speciesSnap.data();
    const forms = Array.isArray(data.forms) ? [...data.forms] : [];

    const updatedForms = forms.map((form, index) =>
      index === row.formIndex
        ? {
            ...form,
            date: normalizeDate(dateData),
          }
        : form,
    );

    await updateDoc(speciesRef, { forms: updatedForms });

    setRows((prev) =>
      prev.map((item) =>
        item.speciesId === row.speciesId && item.formIndex === row.formIndex
          ? {
              ...item,
              date: normalizeDate(dateData),
              form: {
                ...item.form,
                date: normalizeDate(dateData),
              },
            }
          : item,
      ),
    );

    setStatus(`Updated date for ${row.speciesName}.`);
    setLoading(false);
  }

  useEffect(() => {
    scanLocations();
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f6f8] px-6 py-8">
      <div className="mx-auto w-full max-w-5xl bg-white rounded-3xl shadow-xl p-8 space-y-6 border border-slate-200">
        <h1 className="text-2xl text-slate-800">Location Migration</h1>

        <p className="text-sm text-slate-600">
          This page finds forms that do not yet have mapped coordinates. Select
          a location group to apply one mapped location to all matching forms,
          or edit an individual photo below.
        </p>

        <button
          type="button"
          onClick={scanLocations}
          disabled={loading}
          className="bg-[#1f3d2b] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50"
        >
          {loading ? "Scanning..." : "Rescan locations"}
        </button>

        {status && <div className="text-sm text-slate-700">{status}</div>}

        <div className="text-sm text-slate-500">
          Unmapped forms found: {rows.length}
        </div>

        <div className="flex flex-wrap gap-3">
          {locationCounts.map((item) => (
            <button
              key={item.locationKey}
              type="button"
              onClick={() => setSelectedGroupKey(item.locationKey)}
              className={`rounded-2xl border px-4 py-3 text-left hover:bg-slate-50 ${
                selectedGroupKey === item.locationKey
                  ? "border-[#1f3d2b] bg-green-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="text-sm text-slate-800">{item.location}</div>
              <div className="text-xs text-slate-500">
                {item.count} unmapped form{item.count === 1 ? "" : "s"}
              </div>
            </button>
          ))}
        </div>

        {selectedGroupKey && (
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50 space-y-3">
            <div className="text-sm text-slate-700">
              Selected group: {getLocationLabel(selectedGroupKey)}
            </div>

            <button
              type="button"
              onClick={() => setMigrationModal({ mode: "group" })}
              disabled={loading}
              className="bg-[#1f3d2b] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50"
            >
              Apply mapped location to this group
            </button>
          </div>
        )}

        <div className="pt-4 border-t border-slate-200 space-y-4">
          <h2 className="text-lg text-slate-800">
            {selectedGroupKey
              ? `Photos in ${getLocationLabel(selectedGroupKey)}`
              : "All unmapped photos"}
          </h2>

          {visibleRows.length === 0 && (
            <div className="text-sm text-slate-500">No photos to display.</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleRows.map((row) => (
              <div
                key={`${row.speciesId}-${row.formIndex}`}
                className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm"
              >
                <div className="flex gap-4">
                  <div className="w-28 h-28 flex-shrink-0 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                    {row.imageUrl ? (
                      <img
                        src={row.imageUrl}
                        alt={row.speciesName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-xs text-slate-400">No image</div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-1 text-sm">
                    <div className="italic text-slate-800 truncate">
                      {row.speciesName}
                    </div>

                    {row.commonName && (
                      <div className="text-slate-500 truncate">
                        {row.commonName}
                      </div>
                    )}

                    {row.label && (
                      <div>
                        <span className="text-slate-500">Form:</span>{" "}
                        {row.label}
                      </div>
                    )}

                    <div className="space-y-1">
                      <div className="text-slate-500">Date:</div>

                      {(() => {
                        const draftKey = `${row.speciesId}-${row.formIndex}`;
                        const draftDate =
                          dateDrafts[draftKey] || normalizeDate(row.date);

                        return (
                          <div className="space-y-2">
                            <div className="grid grid-cols-[1fr_1fr_1.6fr] gap-2">
                              <select
                                value={draftDate.day}
                                onChange={(e) =>
                                  setDateDrafts((prev) => ({
                                    ...prev,
                                    [draftKey]: {
                                      ...draftDate,
                                      day: e.target.value,
                                    },
                                  }))
                                }
                                disabled={loading}
                                className="w-full border border-slate-300 rounded-xl px-2 py-1.5 text-xs bg-white disabled:opacity-50"
                              >
                                {DAYS.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>

                              <select
                                value={draftDate.month}
                                onChange={(e) =>
                                  setDateDrafts((prev) => ({
                                    ...prev,
                                    [draftKey]: {
                                      ...draftDate,
                                      month: e.target.value,
                                    },
                                  }))
                                }
                                disabled={loading}
                                className="w-full border border-slate-300 rounded-xl px-2 py-1.5 text-xs bg-white disabled:opacity-50"
                              >
                                {MONTHS.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>

                              <select
                                value={draftDate.year}
                                onChange={(e) =>
                                  setDateDrafts((prev) => ({
                                    ...prev,
                                    [draftKey]: {
                                      ...draftDate,
                                      year: e.target.value,
                                    },
                                  }))
                                }
                                disabled={loading}
                                className="w-full border border-slate-300 rounded-xl px-2 py-1.5 text-xs bg-white disabled:opacity-50"
                              >
                                {YEARS.map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <button
                              type="button"
                              onClick={() => saveSingleFormDate(row, draftDate)}
                              disabled={loading}
                              className="rounded-xl border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                            >
                              Save date
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    {row.variant && row.variant !== "NA" && (
                      <div>
                        <span className="text-slate-500">Variant:</span>{" "}
                        {row.variant}
                      </div>
                    )}

                    {row.collections.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {row.collections.map((collectionName) => (
                          <span
                            key={collectionName}
                            className="text-xs bg-slate-100 px-2 py-1 rounded-lg"
                          >
                            {collectionName}
                          </span>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        setMigrationModal({
                          mode: "single",
                          row,
                        })
                      }
                      disabled={loading}
                      className="mt-2 inline-flex max-w-full rounded-xl border border-slate-300 px-3 py-1.5 text-xs text-left hover:bg-slate-50 disabled:opacity-50"
                    >
                      <span className="truncate">
                        Location: {row.location || "No location"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {migrationModal && (
        <LocationPickerModal
          initialLocation={
            migrationModal.mode === "single"
              ? formToLocationData(migrationModal.row.form)
              : null
          }
          onClose={() => setMigrationModal(null)}
          onConfirm={(locationData) => {
            if (migrationModal.mode === "group") {
              applyLocationToGroup(locationData);
            }

            if (migrationModal.mode === "single") {
              applyLocationToSingleForm(migrationModal.row, locationData);
            }
          }}
        />
      )}
    </div>
  );
}
